import { Box, Drawer, Grid2, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";
import { TicketTypeEnum } from "../../_common/enum/ticketType.enum";
import { StandardButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { StandardList } from "../../_components/list/standardList.component";
import { StandardListItem } from "../../_components/list/standardListItem.component";
import { StandardTextValidator } from "../../_components/textValidator/standardTextValidator";
import { toastr } from "../../_components/toastr/toastr";
import { splitWordOnCamelCase } from "../../_helpers/format.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { clientPriceBandService } from "../../_services/clientPriceBand.service";
import { dropdownService } from "../../_services/dropdown.service";

type TicketActionListOption = { id: number; name: string };

type TicketTypeGroup = {
    id: number;
    name: string;
    actionLists: TicketActionListOption[];
};

export const ClientPriceBandManageDrawer = (params: { isOpen?: boolean, onSave(): void, onClose(): void }) => {

    const resetData = () => ({});

    const { id } = useParams<{ id: string }>();

    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const [prices, setPrices] = useState<Record<number, string>>({});
    const [ticketTypeGroups, setTicketTypeGroups] = useState<TicketTypeGroup[]>([]);

    const ticketTypes = Object.entries(TicketTypeEnum)
        .filter(([, value]) => typeof value === "number")
        .map(([key, value]) => ({
            id: value as number,
            name: splitWordOnCamelCase(key)
        }));

    useEffect(() => {
        fetchData();
    }, [id]);

    useEffect(() => {
        if (isOpen == false)
            setIsOpen(true);
    }, []);

    function fetchData() {
        setIsLoading(true);

        const actionListPromise = Promise.all(
            ticketTypes.map((ticketType) =>
                dropdownService
                    .fetchData(DataTypeEnum.TicketActionLists, ticketType.id)
                    .then((actionLists: TicketActionListOption[]) => ({
                        ...ticketType,
                        actionLists: actionLists || []
                    }))
            )
        ).then((groups) => setTicketTypeGroups(groups.filter(g => g.actionLists.length > 0)));

        const priceBandPromise = id
            ? clientPriceBandService.get(id).then((json) => {
                setData(json);
                const items: Record<number, string> = {};
                for (const _item of json.clientPriceBandItems || []) {
                    items[_item.ticketActionListId] = _item.price?.toString() ?? '';
                }
                setPrices(items);
            })
            : Promise.resolve().then(() => {
                setData(resetData);
                setPrices({});
            });

        return Promise.all([actionListPromise, priceBandPromise])
            .catch(handleErrorResponse)
            .finally(() => setIsLoading(false));
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        const clientPriceBandItems = ticketTypeGroups
            .flatMap(group => group.actionLists)
            .filter(actionList => prices[actionList.id] !== undefined && prices[actionList.id] !== '')
            .map(actionList => {
                const existingItem = data.clientPriceBandItems?.find((i: any) => i.ticketActionListId === actionList.id);

                return {
                    id: existingItem?.id ?? 0,
                    name: actionList.name,
                    ticketActionListId: actionList.id,
                    price: parseFloat(prices[actionList.id])
                };
            });

        const _data = { ...data, clientPriceBandItems };

        return clientPriceBandService
            .submit(_data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }


    function disable() {
        if (!id) return;
        setIsLoading(true);
        return clientPriceBandService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    const handlePriceChange = (actionListId: number, value: string) => {
        setPrices(_data => ({ ..._data, [actionListId]: value }));
    };


    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={isOpen}
            ModalProps={{
                keepMounted: true
            }}>

            <DrawerHeader name={(!!id) ? "Update Client Price Band" : "Create Client Price Band"} onClose={params.onClose} />

            <ValidatorForm
                onSubmit={submit}
                noValidate>

                <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                    <Grid2 size={{ xs: 12 }}>
                        <StandardTextValidator
                            key="name"
                            name="name"
                            label="Name"
                            required
                            fullWidth
                            validators={['required', 'minStringLength:1']}
                            errorMessages={['required field', "Field cannot be empty"]}
                            value={data.name}
                            onChange={handleChange}
                        />
                    </Grid2>

                    <StandardList isLoading={isLoading}>
                        {!isLoading && ticketTypeGroups.map((group) => (
                            <Grid2 size={{ xs: 12 }} key={group.id}>
                                <Box sx={{ px: 1, py: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {group.name}
                                    </Typography>
                                </Box>
                                {group.actionLists.map((actionList) => (
                                    <StandardListItem
                                        primary={actionList.name}
                                        secondary={
                                            <StandardTextValidator
                                                key={`price-${actionList.id}`}
                                                name={`price-${actionList.id}`}
                                                label="Price"
                                                fullWidth
                                                type="number"
                                                validators={['minNumber:0']}
                                                errorMessages={['Must be >= 0']}
                                                value={prices[actionList.id] ?? ''}
                                                onChange={(e: any) => handlePriceChange(actionList.id, e.target.value)}
                                            />
                                        }
                                        key={`${group.id}-${actionList.id}`}
                                    />
                                ))}
                            </Grid2>
                        ))}
                    </StandardList>

                    <Grid2 size={{ xs: 12 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: data.id == null ? 12 : data?.isActive === false ? 12 : 6 }}>
                                <StandardButton
                                    saving={saving}
                                    loading={isLoading}
                                    icon="save"
                                    type="submit">
                                    {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                </StandardButton>
                            </Grid2>

                            {(!!id && data?.isActive === true) &&
                                <Grid2 size={{ xs: 6 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="block"
                                        variant="error"
                                        onClick={disable}>
                                        Disable
                                    </StandardButton>
                                </Grid2>
                            }
                        </Grid2>
                    </Grid2>
                </Grid2>

            </ValidatorForm>
        </Drawer>
    );
};
